import { HttpHeaders } from '@angular/common/http';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure } from '@ptl/account-management';
import { BrandingConfigurationService } from '@ptl/brand';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PortalLinkService } from '../../producer/ssos/portal-link.service';
import { PROSPECT_API_ENUM } from '../enums/prospect-api.enum';
import { Prospect as ProducerRetrievedProspect } from '../interfaces/on-exchange-prospects-retrieval.producer.response.interface';
import { Prospect as ConsumerRetrievedProspect } from '../interfaces/prospect.interface';

enum ONX_SSO_URL_ENUM {
  QA = 'https://imp1a.healthcare.gov/marketplace/brokerService',
  QA2 = 'https://uat0.healthcare.gov/marketplace/brokerService',
  PRODUCTION = 'https://www.healthcare.gov/marketplace/brokerService'
}

@Injectable({
  providedIn: 'root'
})
export class FFMRedirectService {

  renderer: Renderer2 = null;

  constructor(
    private brandService: BrandingConfigurationService,
    private httpClient: HTTPWrapperContext,
    private portalLinkService: PortalLinkService,
    private rendererFactory: RendererFactory2,
    private reducerUtilityService: ReducerUtilityService,
    private commonUtilityService: CommonUtilityService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  async getONXConfig(): Promise<{
    Information_exchange_system_id?: string,
    NameID?: string,
    Keep_alive_url?: string,
    Return_url?: string,
    TransferType?: string,
    [key: string]: string
  }> {
    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: '/cmcrst/x-services/applicationconfigurationvalues/list/CONEXCHANGE',
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      })
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    const configValues = await this.httpClient.makeRequest<any>(requestConfigImplementation);

    const appConfigValues: { [key: string]: any } = {};

    if (configValues?.payload && Object.prototype.toString.call(configValues.payload.values) === '[object Array]') {
        configValues.payload.values.forEach((item) => {
            appConfigValues[item.key] = item.value;
        });
    }

    return appConfigValues;
  }

  async restructureDataForFFM(data: {
    userType: string;
    state: string;
    ffmAssignedConsumerID: string;
    partnerAssignedConsumerID: string;
    firstName: string;
    middleName: string;
    lastName: string;
    address?: string;
    address1?: string;
    address2?: string;
    dob: number;
    city: string;
    ffmId?: unknown;
    npnNo?: unknown;
  }): Promise<{
    InformationExchangeSystemID: unknown,
    NameID: unknown,
    KeepAliveURL: unknown,
    ReturnURL: unknown,
    TransferType: unknown,
    FirstName?: unknown,
    LastName?: unknown,
    MiddleName?: unknown,
    DateOfBirth?: unknown,
    State?: unknown,
    StateExchangeCode?: unknown,
    UserType?: unknown,
    FFEAssignedConsumerID?: unknown,
    PartnerAssignedConsumerID?: unknown,
    StreetName1?: unknown,
    StreetName2?: unknown,
    CityName?: unknown,
    ZipCode?: unknown,
    PhoneNumber?: unknown,
    Email?: unknown,
    FFEUserID?: unknown,
    NPN?: unknown
  }> {


    const returnData = await this.getONXConfig();


    /** @TODO build an interface for this payload */
    const payload: {
      InformationExchangeSystemID: unknown,
      NameID: unknown,
      KeepAliveURL: unknown,
      ReturnURL: unknown,
      TransferType: unknown,
      FirstName?: unknown,
      LastName?: unknown,
      MiddleName?: unknown,
      DateOfBirth?: unknown,
      State?: unknown,
      StateExchangeCode?: unknown,
      UserType?: unknown,
      FFEAssignedConsumerID?: unknown,
      PartnerAssignedConsumerID?: unknown,
      StreetName1?: unknown,
      StreetName2?: unknown,
      CityName?: unknown,
      ZipCode?: unknown,
      PhoneNumber?: unknown,
      Email?: unknown,
      FFEUserID?: unknown,
      NPN?: unknown,
    } = {
      InformationExchangeSystemID: returnData.Information_exchange_system_id, // static,
      NameID: returnData.NameID,
      KeepAliveURL: `${window.location.origin}${returnData.Keep_alive_url}`,
      ReturnURL: `${window.location.origin}${returnData.Return_url}`,
      TransferType: returnData.TransferType // Not sure what this will come from, maybe it is static,
    };

    /** Producer stuff ( may use visitor ) */
    if (data.userType) {
      // Old comment mentions "According to Mohan this will always be Agent for producer on exchange"
      payload.UserType = data.userType;
    }
    if (data.ffmId) {
      payload.FFEUserID = data.ffmId;
    }
    if (data.npnNo) {
      payload.NPN = data.npnNo;
    }
    /** End producer stuff */

    if (data.firstName) {
      payload.FirstName = data.firstName;
    }
    if (data.lastName) {
      payload.LastName = data.lastName;
    }
    if (data.middleName) {
      payload.MiddleName = data.middleName;
    }
    if (data.dob) {
      payload.DateOfBirth = data.dob;
    }

    if (data.state) {
      payload.State = data.state;
      payload.StateExchangeCode = data.state + '0';
    }


    if (data.ffmAssignedConsumerID) {
      payload.FFEAssignedConsumerID = data.ffmAssignedConsumerID;
    }

    if (data.partnerAssignedConsumerID) {
      payload.PartnerAssignedConsumerID = data.partnerAssignedConsumerID;
    }
    if (data.address1) {
      payload.StreetName1 = data.address1;
    }
    if (data.address2) {
      payload.StreetName2 = data.address2;
    }

    /**
     * ( comment made by Devon before 2022-10-17: These sections are from old HCR, tracing the data - these values were NEVER passed so, effectively dead  )
     * ( comment made by Devon after 2022-10-17: wtf, so, CityName is 100% required to get to FFM - apparently - the rest didn't made to the interfaces I made;
     * I'm not sure if these are deprecated fields from a Salesforce(which I've heard is retired, and thus Pay by phone might also be retired, in favor of Agent Cubed ) )
     */
    if (data.city) {
      payload.CityName = data.city;
    }
    // if (data.zipCode) {
    //   payload.ZipCode = data.zipCode;
    // }
    // if (data.homePhone) {
    //   payload.PhoneNumber = data.homePhone;
    // }
    // if (data.email) {
    //   payload.Email = data.email;
    // }

    return payload;
  }

  async persistTransferActivity(prospectId: string) {

    const serviceParams = {
      PartnerAssignedConsumerID: prospectId,
      TransferType: 'Transfer Consumer to FFM'
    };
    const requestPayload = JSON.stringify({
      baseMessage: {
        headerVersion: '1.0',
        payloadVersion: '2.0',
        additionalInfo: null
      },
      payload: serviceParams
    });

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: PROSPECT_API_ENUM.PROSPECT_STATUS_UPDATE,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      POST_OR_PUT_PAYLOAD: requestPayload
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest<any>(requestConfigImplementation);
  }

  async redirectToFFM(
    postData: {
      userType: string;
      state: string;
      ffmAssignedConsumerID: string;
      partnerAssignedConsumerID: string;
      firstName: string;
      middleName: string;
      lastName: string;
      address?: string;
      address1?: string;
      address2?: string;
      dob: number;
      city: string;
      ffmId?: unknown;
      npnNo?: unknown;
    },
    loginPageOnly?: boolean
  ) {
      //   {
      //     "prospect": {
      //         "InformationExchangeSystemID": "04.BCS.WY*.321.288",
      //         "NameID": "04.BCS.WY*.321.288",
      //         "KeepAliveURL": "https://shoptenv3.yourwyoblue.com/cmcrst/x-services/securehealthcheck",
      //         "ReturnURL": "https://shoptenv3.yourwyoblue.com/cmcrst/x-services/consumers/response",
      //         "TransferType": "Direct Enrollment",
      //         "FirstName": "WYPORTALTWOTHREE",
      //         "LastName": "WYPORTALTWOTHREE",
      //         "DateOfBirth": 631152000000,
      //         "CityName": "FAKE",
      //         "State": "WY",
      //         "StateExchangeCode": "WY0",
      //         "UserType": "Consumer",
      //         "FFEAssignedConsumerID": "18771783",
      //         "PartnerAssignedConsumerID": "PROS599459"
      //     }
      // }

      // postData should contain .PartnerAssignedConsumerID:prospectId
    const ffmReadyData = await this.restructureDataForFFM(postData);
    if (loginPageOnly) {
      ffmReadyData.TransferType = 'login';
    }
    const payload = JSON.stringify({ prospect: ffmReadyData });

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/dpsext/x-services/hcr/v1/saml',
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      responseType: 'text',
      POST_OR_PUT_PAYLOAD: payload
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);

    let cookies = document.cookie;
    let cookiesSplit = cookies.split('; ');
    let cookieMap = {};
    cookiesSplit.forEach(function(c) {
        let kvp = c.split('=');
        cookieMap[kvp[0]] = kvp[1];
    });

    const LTPA_TOKEN_KEY = 'LtpaToken2';
    const SPECIFIC_DOMAIN = '.' + window.location.hostname;
    const GENERIC_DOMAIN = (() => {
      let split = window.location.hostname.split('.');
      split.shift(); // remove first item
      return '.' + split.join('.');
    })();
    const COOKIES_EXPIRATION_NOW = 'Thu, 01 Jan 1970 00:00:00 UTC';

    /** Remove LTPA tokens ( might be 2, we NEED only ONE, specifically the more specific one ) */
    // create a .producer.yourwyoblue.com cookie named LtpaToken2
    window.document.cookie = `${LTPA_TOKEN_KEY}=${cookieMap[LTPA_TOKEN_KEY]};domain=${SPECIFIC_DOMAIN};path=/;secure`;
    // delete a .yourwyoblue.com cookie named LtpaToken2
    window.document.cookie = `${LTPA_TOKEN_KEY}=;domain=${GENERIC_DOMAIN};path=/;expires=${COOKIES_EXPIRATION_NOW};secure`;

    // Do the SAML request
    const samlResponse = await this.httpClient.makeRequest<any>(requestConfigImplementation);

    // create a .yourwyoblue.com cookie named LtpaToken2 - uncomment this if, in the first tab, services stop working
    // window.document.cookie = `${LTPA_TOKEN_KEY}=${cookieMap[LTPA_TOKEN_KEY]};domain=${GENERIC_DOMAIN};path=/;secure`;

    const samlPostData = samlResponse.replace('SAMLResponse=', '').trim();

    const windowName = 'HMFFMWindow';
    // const win = window.open('/blank.html', windowName);
    // const win = window.open('about:blank', windowName);

    /** @TODO implement session time extension when session windows are active */
    // loginManager.addSessionWindow(win);

    const formAction = !(
      window.location.origin.includes('denv') ||
      window.location.origin.includes('tenv')
    ) ? ONX_SSO_URL_ENUM.PRODUCTION : ONX_SSO_URL_ENUM.QA2;

    const inputElement: HTMLInputElement = this.renderer.createElement('input');
    inputElement.id = 'samlResponse';
    inputElement.name = 'SAMLResponse';
    inputElement.type = 'hidden';
    inputElement.value = samlPostData;

    const formElement: HTMLFormElement = this.renderer.createElement('form');
    formElement.id = 'samlResponseForm';
    formElement.method = 'post';
    formElement.action = formAction;
    // formElement.target = windowName;

    const divElement: HTMLDivElement = this.renderer.createElement('div');
    divElement.id = 'samlFormRedirect';

    formElement.appendChild(inputElement);
    divElement.appendChild(formElement);

    /**
     * We need to delete this form before trying to use it a second time, since it loses reference after we submit and try to make a new one
     */
    if (!document.getElementById('samlFormRedirect')) {
      document.getElementsByTagName('body')[0].appendChild(divElement);
    } else {
      /** remove old form */
      const oldForm = document.getElementById('samlFormRedirect');
      oldForm.remove();

      /** add new form */
      document.getElementsByTagName('body')[0].appendChild(divElement);
    }


    formElement.submit();

  }

  async goToMarketplace(
    selectedProspect: ConsumerRetrievedProspect | ProducerRetrievedProspect,
    prospects: ConsumerRetrievedProspect[] | ProducerRetrievedProspect[],
    loginPageOnly?: boolean
  ) {
    const loginInfoState: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(
      LOGIN_INFO_REDUCER_NAME
    );

    const ffmForward = async () => {
      let postData = null;
      prospects.forEach(prospect => {
        if (prospect.prospectId === selectedProspect.prospectId) {
          postData = {
            userType: this.commonUtilityService.isAgent() ? 'Agent' : 'Consumer',
            state: prospect.state,
            ffmAssignedConsumerID: prospect.consumerFFMId,
            partnerAssignedConsumerID: prospect.prospectId,
            firstName: loginInfoState.userInfo.firstName,
            middleName: '',
            lastName: loginInfoState.userInfo.lastName,
            address: prospect.address1,
            dob: prospect.dob,
            city: prospect.city
          };
          if (loginInfoState.userInfo.ffmId) {
            postData.ffmId = loginInfoState.userInfo.ffmId;
          }
          if (loginInfoState.userInfo.npnNo) {
            postData.npnNo = loginInfoState.userInfo.npnNo;
          }
        }
      });
      const returnable = await this.redirectToFFM(postData, loginPageOnly);
      return returnable;
    };

    await this.persistTransferActivity(selectedProspect.prospectId);
    await ffmForward();
  }
}
